import type { FmElement } from './elements.types';

/** The text an element is known by, so a slot can key its elements without positions. */
function elementSignature(element: FmElement): string {
  if (element.testId) return element.testId;
  switch (element.element) {
    case 'copy':
      return element.heading ?? element.lede ?? element.eyebrow ?? '';
    case 'document':
      return element.title;
    case 'chart':
    case 'table':
    case 'rankedList':
      return element.title ?? '';
    case 'notice':
      return element.title;
    case 'lens':
      return element.control.id;
    case 'provenance':
      return element.evidence.id;
    case 'keyFigures':
      return element.figures[0].label;
    default:
      return '';
  }
}

/**
 * Keys for the elements of one slot. Elements carry no id; the type plus the
 * text an element is known by identifies it, and a repeat of the same
 * signature is numbered by occurrence so the key survives a sibling moving.
 */
export function keyedElements<Element extends FmElement>(
  elements: readonly Element[],
): Array<{ key: string; element: Element }> {
  const seen = new Map<string, number>();
  return elements.map((element) => {
    const signature = `${element.element}:${elementSignature(element)}`;
    const occurrence = seen.get(signature) ?? 0;
    seen.set(signature, occurrence + 1);
    return { key: occurrence === 0 ? signature : `${signature}#${String(occurrence)}`, element };
  });
}
